import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './Icon';
import { api } from '../api';
import type { MessageDetail } from '../types';
import { attachmentKind, avatarHue, fileSize, fullDate, initials } from '../lib/format';

interface Props {
  message: MessageDetail;
  /** Qoşma endirmək üçün qovluq yolu lazımdır — UID tək başına kifayət etmir. */
  path: string;
  onBack: () => void;
  onDelete: () => void;
  onReply: () => void;
  /** `null` — başqa alıcı yoxdur, düymə gizlədilir. */
  onReplyAll: (() => void) | null;
  onForward: () => void;
  onArchive: () => void;
  onSpam: () => void;
  /**
   * Spam qovluğundayıqsa düymə «Spam deyil» olur və məktub Gələnlərə qayıdır.
   * ⚠️ Bunsuz Spam qovluğunda «Spam» düyməsi məktubu ELƏ ORAYA köçürməyə
   * çalışırdı — heç nə olmurdu və istifadəçi səbəbini anlamırdı.
   */
  inJunk: boolean;
  /** Səbətdəyiksə «Bərpa et» görünür. */
  inTrash: boolean;
  onRestore: () => void;
  /** Oxunmamış işarələ — oxucudan çıxır və siyahıda qalın qayıdır. */
  onMarkUnread: () => void;
  /** İstifadəçinin öz qovluqları — «Qovluğa köçür» menyusu üçün. */
  customFolders: { path: string; label: string }[];
  onMoveTo: (dest: { path: string; label: string }) => void;
}

export function MessageView({
  message: m,
  path,
  onBack,
  onDelete,
  onReply,
  onReplyAll,
  onForward,
  onArchive,
  onSpam,
  inJunk,
  inTrash,
  onRestore,
  onMarkUnread,
  customFolders,
  onMoveTo,
}: Props): React.ReactElement {
  const from = m.from ?? { name: '', address: '(naməlum)' };

  /**
   * Uzaq şəkillər — SAYAN VƏ İCAZƏ VERƏN MEXANİZM.
   *
   * ⚠️ ƏVVƏL UZAQ ŞƏKİLLƏR ƏBƏDİ BLOKLANIRDI. CSP-də yalnız `img-src data:`
   * vardı və istifadəçinin onu açmaq imkanı YOX İDİ. Nəticədə loqolu hər
   * məktub — bank bildirişi, təchizatçı hesab-fakturası, hər bülleten —
   * sınıq şəkil qutuları ilə görünürdü. Bu, «bu proqram məktubu göstərə
   * bilmir» kimi oxunur.
   *
   * Blokun ÖZÜ düzgündür: uzaq şəkil göndərənə məktubu açdığını, vaxtını və
   * IP-ni bildirən izləyicidir. Nöqsan blok deyil, çıxış yolunun olmaması
   * idi. Proton, Gmail və Outlook da belə edir — bloklayır, amma soruşur.
   */
  const remoteCount = useMemo(() => {
    if (!m.html) return 0;
    const srcs = m.html.match(/<img[^>]+src=["']https?:/gi)?.length ?? 0;
    const bg = m.html.match(/url\(\s*["']?https?:/gi)?.length ?? 0;
    return srcs + bg;
  }, [m.html]);

  /**
   * Göndərənə görə yadda saxlanılan icazə.
   *
   * ⚠️ Hazırda yalnız bu brauzerdə saxlanılır. Server tərəfi ayar anbarı
   * yaranana qədər başqa cihazda təkrar soruşulacaq — bu, məlumat itkisi
   * deyil, sadəcə əlavə bir klikdir.
   */
  const senderKey = `mailora.img.${(m.from?.address ?? '').toLowerCase()}`;
  const [showRemote, setShowRemote] = useState(
    () => localStorage.getItem(senderKey) === '1',
  );

  // Başqa məktuba keçiləndə vəziyyət sıfırlanmalıdır — yoxsa bir məktubda
  // verilən icazə növbətidə də qüvvədə qalardı.
  useEffect(() => {
    setShowRemote(localStorage.getItem(senderKey) === '1');
  }, [senderKey, m.uid]);

  /**
   * HTML məktublar sandbox-lu iframe-də göstərilir.
   *
   * Bu, danışıla bilməz bir tələbdir: məktubun HTML-i tamamilə etibarsız
   * girişdir. Birbaşa DOM-a qoysaq, göndərən bizim panelimizdə skript
   * işlədə və sessiyanı ələ keçirə bilər.
   *
   * ⚠️ `sandbox="allow-same-origin"` — `allow-scripts` OLMADAN.
   *
   * Bu cütlük qəsdəndir və SIRASI ÖNƏMLİDİR:
   *   • `allow-scripts` YOXDUR → məktubun içində heç bir skript işləmir;
   *   • `allow-same-origin` VAR → valideyn çərçivənin hündürlüyünü ÖLÇƏ bilir.
   *
   * ⚠️ İKİSİNİ BİRLİKDƏ VERMƏK OLMAZ. `allow-scripts allow-same-origin`
   * sandbox-u tamamilə mənasız edir — məktubdakı skript valideyn sənədə
   * çıxış əldə edir və sessiyanı oğurlaya bilər. Buraya `allow-scripts`
   * əlavə edən hər kəs bu şərhi oxumalıdır.
   */
  const srcDoc = useMemo(() => {
    if (!m.html) return null;
    const img = showRemote ? 'data: https:' : 'data:';

    /*
      ⚠️ `overflow-wrap: anywhere` — UZUN BOŞLUQSUZ SƏTİRLƏR ÜÇÜN.
      Real hadisə (2026-08-12): ödəniş inteqrasiyası məktubunda 80
      simvolluq gizli açar tək parça idi. Sətir keçidi olmadığı üçün
      çərçivədən daşır və SAĞDAN KƏSİLİRDİ — istifadəçi açarın sonunu
      nə görür, nə də kopyalaya bilirdi. Eyni hal uzun URL-lərdə,
      sifariş nömrələrində və izləmə kodlarında da olur.

      `break-word` deyil, `anywhere`: birincisi yalnız söz KEÇMƏSƏ
      işləyir və cədvəl xanasının minimum enini hesablayanda nəzərə
      alınmır, ona görə cədvəlli məktublarda daşma qalırdı.

      ⚠️ `table` ÜÇÜN AYRICA QAYDA YOXDUR — QƏSDƏN. Sabit enli imza
      cədvəlini zorla daraltmaq göndərənin dizaynını pozur. Belə
      hallarda `.reader-paper` üfüqi sürüşdürmə verir (styles.css).
    */
    const blockedImg = showRemote
      ? ''
      : /*
          ⚠️ BLOKLANMIŞ ŞƏKİL «SINIQ FAYL» KİMİ GÖRÜNMƏMƏLİDİR.
          CSP uzaq şəkli dayandıranda brauzer öz qırıq ikonunu çəkir —
          məktub xarab kimi görünür və istifadəçi səbəbini anlamır.
          Element hələ DOM-dadır, ona görə onu atribut seçicisi ilə
          tutub sakit yer tutucuya çeviririk. Ölçüsü saxlanılır ki,
          «Şəkilləri göstər» basılanda düzülüş sıçramasın.
        */
        `img[src^="http"]{background:#f6f7f9;border:1px dashed #cbd1d8;border-radius:4px;` +
        `min-width:16px;min-height:16px}`;

    return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${img}; style-src 'unsafe-inline'">
<style>html,body{margin:0}body{font:14px/1.6 system-ui,sans-serif;padding:16px;color:#16181d;background:#fff;overflow-wrap:anywhere}
img{max-width:100%;height:auto}a{color:#0b63c5}${blockedImg}</style></head><body>${m.html}</body></html>`;
  }, [m.html, showRemote]);

  /**
   * Çərçivənin hündürlüyünü məzmununa uyğunlaşdırır.
   *
   * ⚠️ ƏVVƏL SABİT 420px İDİ. İki sətirlik məktub 420px ağ boşluq kimi
   * görünürdü, uzun bülleten isə səhifənin içində İKİNCİ sürüşdürmə zolağı
   * yaradırdı — hər ikisi interfeysi yarımçıq göstərir.
   */
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [frameH, setFrameH] = useState(120);

  const measure = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.body) return;
    // `scrollHeight` şəkillər yüklənənə qədər dəyişir, ona görə bir neçə
    // dəfə ölçülür (aşağıda `load` və taymer).
    const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
    if (h > 0) setFrameH(Math.min(h + 8, 20000));
  }, []);

  useEffect(() => {
    if (!srcDoc) return;
    // Şəkillər gec yüklənir — bir neçə dəfə yenidən ölçürük.
    const timers = [80, 300, 900, 2000].map((ms) => setTimeout(measure, ms));
    return () => timers.forEach(clearTimeout);
  }, [srcDoc, measure]);

  return (
    <article className="reader">
      <header className="reader-bar">
        <button className="btn-icon" onClick={onBack} aria-label="Geri">
          <Icon name="back" size={18} />
        </button>
        <div className="reader-actions">
          {/*
            ⚠️ CAVAB VƏ YÖNLƏNDİR ƏSAS ƏMƏLİYYATLARDIR — solda və qabarıq.
            Arxiv/Spam/Sil isə nadir və geri qaytarılması çətin əməliyyatlardır;
            onlar sağda, sönük qalır. Əvvəl hamısı eyni görünürdü və «Sil»
            «Cavab yaz» qədər asan basılırdı.
          */}
          <button className="btn-text is-primary" onClick={onReply}>
            Cavab yaz
          </button>
          {onReplyAll && (
            <button className="btn-text is-primary" onClick={onReplyAll}>
              Hamısına cavab
            </button>
          )}
          <button className="btn-text is-primary" onClick={onForward}>
            Yönləndir
          </button>
          <span className="reader-gap" />

          {/*
            ⚠️ SƏBƏTDƏ «BƏRPA ET» BİRİNCİ GƏLİR. Səbətə düşmüş məktubu
            açan adam onu çox güman geri istəyir — «Arxivə» yox.
          */}
          {inTrash && (
            <button className="btn-text is-primary" onClick={onRestore}>
              Bərpa et
            </button>
          )}

          <button className="btn-text" onClick={onMarkUnread} title="Oxunmamış işarələ">
            Oxunmamış
          </button>

          {/*
            ⚠️ ÖZ QOVLUQLARI YALNIZ VARSA GÖSTƏRİLİR. Boş açılan menyu
            «burada nəsə olmalıydı» hissi verir; qovluğu olmayan istifadəçi
            üçün düymə ümumiyyətlə görünməməlidir.
          */}
          {customFolders.length > 0 && (
            <details className="reader-move">
              <summary className="btn-text">Qovluğa köçür</summary>
              <ul className="reader-move-menu">
                {customFolders.map((f) => (
                  <li key={f.path}>
                    <button type="button" onClick={() => onMoveTo(f)}>
                      {f.label}
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {!inTrash && (
            <button className="btn-text" onClick={onArchive}>
              Arxivə
            </button>
          )}

          {/*
            ⚠️ SPAM QOVLUĞUNDA DÜYMƏ TƏRSİNƏ İŞLƏYİR.
            Əvvəl orada da «Spam» yazırdı və məktubu elə Spam qovluğuna
            köçürməyə çalışırdı — nəticəsiz əməliyyat. İndi «Spam deyil»
            olur və Gələnlərə qaytarır; bu, həm istifadəçi üçün doğru
            hərəkətdir, həm də süzgəcə müsbət siqnal verir.
          */}
          <button className="btn-text" onClick={inJunk ? onRestore : onSpam}>
            {inJunk ? 'Spam deyil' : 'Spam'}
          </button>

          {/*
            ÇAP — brauzerin öz pəncərəsi.
            ⚠️ `window.print()` BÜTÜN səhifəni çap edərdi (yan panel, rail).
            `@media print` qaydaları ilə yalnız oxucu buraxılır.
          */}
          <button className="btn-text" onClick={() => window.print()} title="Çap et">
            Çap
          </button>

          <button className="btn-text btn-danger" onClick={onDelete}>
            {inTrash || inJunk ? 'Həmişəlik sil' : 'Sil'}
          </button>
        </div>
      </header>

      <div className="reader-body">
        {/*
          ⚠️ YER TUTUCU YALNIZ GÖRÜNTÜDƏ. Arxa tərəf boş mövzunu boş
          qaytarır (bax mail/service.ts) — yer tutucunu məlumata qarışdırsaq,
          o, «Re: (mövzusuz)» kimi kənara gedirdi.
        */}
        <h1 className="reader-subject">
          {m.subject || <span className="msg-nosubject">(mövzusuz)</span>}
        </h1>

        <div className="reader-meta">
          <span
            className="msg-avatar"
            style={{ background: `hsl(${avatarHue(from.address)} var(--avatar-s) var(--avatar-l))` }}
            aria-hidden="true"
          >
            {initials(from.name, from.address)}
          </span>
          <div className="reader-who">
            <div className="reader-from">
              <strong>{from.name || from.address}</strong>
              {from.name && <span className="reader-addr">&lt;{from.address}&gt;</span>}
            </div>
            <div className="reader-to">
              Kimə: {m.to.map((t) => t.address).join(', ') || '—'}
              {m.cc.length > 0 && ` · Kopya: ${m.cc.map((c) => c.address).join(', ')}`}
            </div>
          </div>
          <time className="reader-date">{fullDate(m.date)}</time>
        </div>

        {m.attachments.length > 0 && (
          <div className="reader-attachments">
            {m.attachments.map((a, i) => (
              // Adi keçid — `fetch` DEYİL. Brauzerin öz endirmə mexanizmi
              // böyük faylı yaddaşa yığmadan birbaşa diskə yazır.
              <a
                className="chip chip-lg"
                key={i}
                href={api.attachmentUrl(path, m.uid, a.part)}
                download={a.filename}
                title={`${a.filename} — endir`}
              >
                <span className="chip-name">{a.filename}</span>
                <span className="chip-kind">{attachmentKind(a.type, a.filename)}</span>
                <span className="chip-size">{fileSize(a.size)}</span>
              </a>
            ))}
          </div>
        )}

        {srcDoc ? (
          <>
            {remoteCount > 0 && !showRemote && (
              <div className="img-block" role="status">
                <span className="img-block-text">
                  Məxfilik üçün {remoteCount} şəkil göstərilmir — uzaq şəkillər
                  göndərənə məktubu açdığınızı bildirir.
                </span>
                <button className="btn-text" onClick={() => setShowRemote(true)}>
                  Şəkilləri göstər
                </button>
                <button
                  className="btn-text"
                  onClick={() => {
                    localStorage.setItem(senderKey, '1');
                    setShowRemote(true);
                  }}
                >
                  Bu göndərəndən həmişə
                </button>
              </div>
            )}
            {/*
              ⚠️ ÇƏRÇİVƏ «KAĞIZ» KİMİ RAMKALANIR.
              Göndərənin HTML-i öz rənglərini gətirir və demək olar həmişə
              ağ fonludur. Qaranlıq rejimdə bu, ekranın ortasında kəskin
              kənarlı parlaq lövhə yaradır — qaranlıq rejimin «sınıq»
              görünməsinin əsas səbəbi budur.
              HTML-i çevirmək OLMAZ: göndərənin dizaynını pozar, kontrast
              itər, loqolar yanlış fonda qalar. Əvəzinə onu sənəd kimi
              çərçivəyə salırıq — «məktub göndərənin qurduğu kimi» oxunur.
            */}
            <div className="reader-paper">
              <iframe
                ref={frameRef}
                className="reader-html"
                title="Məktubun məzmunu"
                sandbox="allow-same-origin"
                srcDoc={srcDoc}
                style={{ height: `${frameH}px` }}
                onLoad={measure}
              />
            </div>
          </>
        ) : (
          <pre className="reader-text">{m.text ?? '(boş məktub)'}</pre>
        )}
      </div>
    </article>
  );
}
